import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ThemeEditor } from '@/components/ThemeEditor';
import { ThemePreview } from '@/components/ThemePreview';
import { CustomFieldEditor } from '@/components/CustomFieldEditor';
import { useEvents } from '@/hooks/useEvents';
import { useTheme } from '@/hooks/useTheme';
import { ThemeConfig, CustomField } from '@/types';

export function CreateEvent() {
  const navigate = useNavigate();
  const { createEvent, loading, error } = useEvents();
  const { presetToThemeConfig } = useTheme();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    date: '',
    location: '',
    description: '',
    password: '',
  });
  const [theme, setTheme] = useState<ThemeConfig>(presetToThemeConfig('classic'));
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '').substring(0, 30);
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({ ...prev, name, slug: prev.slug || generateSlug(name) }));
  };

  const handleSubmit = async () => {
    const result = await createEvent({
      name: formData.name,
      slug: formData.slug,
      date: formData.date,
      location: formData.location,
      description: formData.description,
      password: formData.password || undefined,
      theme,
      custom_fields: customFields,
    });
    if (result) {
      navigate(`/events/${result.event_id}/admin`);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.name && formData.slug && formData.date && formData.location;
    return true;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-display">Create Event</h1>
          <p className="text-gray-500 mt-1">Set up your event in a few simple steps</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-10">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${step >= s ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {s}
              </div>
              {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-violet-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
              <input type="text" value={formData.name} onChange={(e) => handleNameChange(e.target.value)} className="input-field" placeholder="Hannah & Adrian's Wedding" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event URL *</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">aboutbloodytime.love/</span>
                <input type="text" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} className="input-field flex-1" placeholder="hannahandadrian" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time *</label>
                <input type="datetime-local" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="input-field" placeholder="The Grand Ballroom, Melbourne" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-field min-h-[120px] resize-y" placeholder="Tell your guests about the event..." rows={4} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Password</label>
              <input type="text" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="input-field" placeholder="Leave blank to auto-generate" />
              <p className="text-xs text-gray-400 mt-1">Guests will need this password to view your event page</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose a Theme</h3>
                <ThemeEditor theme={theme} onChange={setTheme} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                <ThemePreview theme={theme} />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">RSVP Form Fields</h3>
            <p className="text-sm text-gray-500 mb-6">Add custom fields. Name, email, attending, and guest count are always included.</p>
            <CustomFieldEditor fields={customFields} onChange={setCustomFields} />
          </div>
        )}

        {error && <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed">Back</button>
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">Continue</button>
          ) : (
            <button onClick={handleSubmit} disabled={loading || !canProceed()} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">{loading ? 'Creating...' : 'Create Event'}</button>
          )}
        </div>
      </div>
    </Layout>
  );
}
